import React, { useState, useEffect, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import Login from './components/Login';
import Navbar from './components/Navbar';
import Branches from './components/Branches';
import Users from './components/Users';
import Profile from './components/Profile';
import OfflineBanner from './components/OfflineBanner';
import { setupOfflineInterceptor } from './utils/offlineAxios';
import { syncManager } from './utils/syncManager';
import * as LazyComponents from './components/LazyComponents';
import './App.css';

const API_URL = window.location.origin;
axios.defaults.baseURL = API_URL;

// Axios Interceptor für Token-Ablauf
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401 || error.response?.status === 403) {
      console.log('🔒 Token abgelaufen - automatischer Logout');
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      delete axios.defaults.headers.common['Authorization'];
      window.location.href = '/';
    }
    return Promise.reject(error);
  }
);

const LoadingFallback = () => (
  <div style={{
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '400px',
    fontSize: '1.25rem',
    color: '#64748b'
  }}>
    <div>
      <div style={{
        width: '50px',
        height: '50px',
        border: '4px solid #f3f4f6',
        borderTop: '4px solid #667eea',
        borderRadius: '50%',
        animation: 'spin 1s linear infinite',
        margin: '0 auto 1rem'
      }}></div>
      Lädt...
    </div>
  </div>
);

function App() {
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [user, setUser] = useState(null);
  const [tokenLoaded, setTokenLoaded] = useState(false);

  // Setup Offline-Handling
  useEffect(() => {
    setupOfflineInterceptor();
    
    // Listen für Sync-Events
    syncManager.onSync((result) => {
      if (result.success) {
        console.log('Sync erfolgreich - lade Daten neu');
        // Hier könntest du Daten neu laden
      }
    });
  }, []);

  useEffect(() => {
    console.log('App mounted - checking for stored token');
    const storedToken = localStorage.getItem('token');
    console.log('Stored token:', storedToken ? 'exists' : 'not found');
    
    if (storedToken) {
      console.log('Setting token and axios header');
      setToken(storedToken);
      axios.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;
      
      const userData = localStorage.getItem('user');
      if (userData) {
        setUser(JSON.parse(userData));
      }
    }
    
    setTokenLoaded(true);
  }, []);

  useEffect(() => {
    console.log('Token changed:', token ? 'token set' : 'no token');
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      console.log('Axios header set:', axios.defaults.headers.common['Authorization']);
    } else {
      delete axios.defaults.headers.common['Authorization'];
      console.log('Axios header removed');
    }
  }, [token]);

  const handleLogin = (token, user) => {
    setToken(token);
    setUser(user);
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(user));
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  };

  const handleLogout = () => {
    setToken(null);
    setUser(null);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    delete axios.defaults.headers.common['Authorization'];
  };

  if (!tokenLoaded) {
    return <div>Loading...</div>;
  }

  if (!token) {
    return <Login onLogin={handleLogin} />;
  }

  return (
    <Router>
      <OfflineBanner />
      <div className="App">
        <Navbar user={user} onLogout={handleLogout} />
        <main className="main-content">
          {tokenLoaded && (
            <Suspense fallback={<LoadingFallback />}>
              <Routes>
                <Route path="/" element={<Navigate to="/dashboard" replace />} />
                <Route path="/dashboard" element={<LazyComponents.Dashboard />} />
                <Route path="/todos" element={<LazyComponents.Todos />} />
                <Route path="/projects" element={<LazyComponents.Projects />} />
                <Route path="/projects/:projectId" element={<LazyComponents.ProjectDetail />} />
                <Route path="/protocols/:protocolId" element={<LazyComponents.ProtocolDetail />} />
                <Route path="/participants" element={<LazyComponents.Participants />} />
                <Route path="/branches" element={<Branches />} />
                <Route path="/users" element={<Users />} />
                <Route path="/profile" element={<Profile user={user} onUserUpdate={setUser} />} />
                <Route path="/search" element={<LazyComponents.Search />} />
                <Route path="*" element={<Navigate to="/dashboard" />} />
              </Routes>
            </Suspense>
          )}
        </main>
      </div>
    </Router>
  );
}

export default App;